import { useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { strategiesApi } from "@/lib/api";
import { reportsApi, type ReportExecutionMode } from "@/lib/reportsApi";
import { useRememberedExecutionMode } from "@/hooks/useRememberedExecutionMode";
import type { Strategy } from "@/types/strategy";
import { monthKeyOf, monthRange, monthWeekdays, tradingDays } from "./exitStats";
import {
  EMPTY_FILTERS,
  ReportFilterBar,
  applyFilters,
  type ReportFilterState,
} from "./ReportFilters";
import { MonthChips, ReportModeToggle, SummaryGrid } from "./reportWidgets";

/** The reports HUB — one summary section per strategy with an "Open
 * Detail" button to that strategy's own report page (mirrors the
 * operator's reference dashboard: hub page + per-strategy pages). */
export function ReportsPage(): ReactElement {
  const [month, setMonth] = useState<string>(() => monthKeyOf(new Date()));
  const [filters, setFilters] = useState<ReportFilterState>(EMPTY_FILTERS);
  const [mode, setMode] = useRememberedExecutionMode();
  const strategies = useQuery({
    queryKey: ["strategies"],
    queryFn: () => strategiesApi.list(),
  });
  return (
    <main className="mx-auto max-w-6xl px-6 py-6">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="mb-1">Reports</h1>
          <p className="text-sm text-ink-secondary">
            Monthly exit-style comparison per strategy — LIVE vs the two simulated exits. Open a
            strategy&apos;s detail page for its trade-by-trade view.
          </p>
        </div>
        <ReportModeToggle value={mode} onChange={setMode} />
      </header>
      <MonthChips
        active={month}
        onPick={(key) => {
          setMonth(key);
          setFilters(EMPTY_FILTERS);
        }}
        mode={mode}
      />
      {/* One filter bar for the whole page — every strategy section below
          recomputes from the same filtered slice. */}
      <ReportFilterBar filters={filters} days={monthWeekdays(month)} onChange={setFilters} />
      {(strategies.data ?? []).map((s) => (
        <HubSection key={s.id} strategy={s} month={month} filters={filters} mode={mode} />
      ))}
      {strategies.data?.length === 0 && (
        <p className="text-sm text-ink-secondary">No strategies yet.</p>
      )}
    </main>
  );
}

function HubSection({
  strategy,
  month,
  filters,
  mode,
}: Readonly<{
  strategy: Strategy;
  month: string;
  filters: ReportFilterState;
  mode: ReportExecutionMode;
}>): ReactElement {
  const { from, to } = monthRange(month);
  const report = useQuery({
    queryKey: ["exit-comparison", strategy.id, month, mode],
    queryFn: () => reportsApi.exitComparison(from, to, strategy.id, mode),
  });
  const filtered =
    report.data !== undefined
      ? applyFilters(report.data.trades, report.data.vix, filters)
      : undefined;
  const badge =
    report.data !== undefined && filtered !== undefined
      ? `${String(filtered.length)} of ${String(report.data.trades.length)} trades · ${String(tradingDays(filtered))} day(s)`
      : "…";
  return (
    <section className="mb-8">
      <div className="mb-3 flex items-center gap-3">
        <h2 className="text-base font-medium">
          <i className="ti ti-arrows-shuffle mr-1 text-accent" /> {strategy.name}
        </h2>
        <span className="pill bg-surface-2 text-ink-secondary">{badge}</span>
        <Link to={`/reports/${strategy.id}`} className="btn btn-secondary ml-auto text-xs">
          <i className="ti ti-player-play text-xs" /> Open Detail
        </Link>
      </div>
      {report.isPending && <p className="text-sm text-ink-muted">Loading…</p>}
      {report.isError && (
        <p className="text-sm text-danger">Failed to load this month&apos;s report.</p>
      )}
      {filtered?.length === 0 && (
        <p className="py-4 text-sm text-ink-secondary">No trades match the filters.</p>
      )}
      {filtered !== undefined && filtered.length > 0 && (
        <SummaryGrid trades={filtered} strategyId={strategy.id} compact />
      )}
    </section>
  );
}
